import { Container, Title, Text, Stack, Accordion, List } from '@mantine/core';
import { Layout } from '../shared/components/Layout';
import { useSEO } from '../shared/hooks';

export function FAQ() {
  useSEO({
    title: 'FAQ - Najczęstsze pytania',
    description: 'Odpowiedzi na najczęstsze pytania o taryfach energetycznych, kalkulatorze i platformie Taryfy Pradu.',
  });
  return (
    <Layout isAuthenticated={false}>
      <Container size="md" py={80}>
        <Stack gap="xl">
          {/* Header */}
          <div>
            <Title order={1} mb="md">
              Najczęściej zadawane pytania (FAQ)
            </Title>
            <Text size="lg" c="dimmed">
              Znajdź odpowiedzi na najczęstsze pytania dotyczące naszej platformy
              i kalkulatora taryf energetycznych.
            </Text>
          </div>

          {/* FAQ Accordion */}
          <Accordion variant="separated" radius="md">
            {/* Calculator Questions */}
            <Accordion.Item value="how-calculator-works">
              <Accordion.Control>
                <Text fw={600}>Jak działa kalkulator taryf?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">
                  Kalkulator taryf analizuje Twoje zużycie energii elektrycznej i porównuje koszty
                  dla różnych dostępnych taryf (G11, G12, G12w). Możesz wprowadzić dane ręcznie
                  lub skorzystać z automatycznej analizy faktur. System oblicza dokładne koszty
                  na podstawie aktualnych cenników dystrybutorów energii i pokazuje, która taryfa
                  będzie dla Ciebie najtańsza.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="which-tariffs">
              <Accordion.Control>
                <Text fw={600}>Jakie taryfy są porównywane?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  <Text c="dimmed">
                    Obecnie porównujemy trzy główne taryfy dostępne dla gospodarstw domowych:
                  </Text>
                  <Text c="dimmed">
                    <strong>G11</strong> - taryfa jednostrefowa z jedną stałą ceną za energię
                    przez całą dobę. Najlepsza dla użytkowników zużywających energię równomiernie.
                  </Text>
                  <Text c="dimmed">
                    <strong>G12</strong> - taryfa dwustrefowa z niższą ceną w nocy (22:00-6:00
                    w dni robocze, cała doba w weekendy). Korzystna dla osób mogących przenieść
                    część zużycia na godziny nocne.
                  </Text>
                  <Text c="dimmed">
                    <strong>G12w</strong> - taryfa dwustrefowa z niższą ceną w nocy (22:00-13:00
                    i 15:00-17:00 w dni robocze, cała doba w weekendy). Najbardziej elastyczna
                    opcja dla aktywnych użytkowników.
                  </Text>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="data-accuracy">
              <Accordion.Control>
                <Text fw={600}>Skąd pochodzą dane o cenach energii?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">
                  Wszystkie ceny energii i opłaty dystrybucyjne pochodzą bezpośrednio z oficjalnych
                  taryf operatorów sieci dystrybucyjnych (OSD), zatwierdzone przez Urząd Regulacji
                  Energetyki (URE). Dane są aktualizowane regularnie, aby odzwierciedlać aktualne
                  ceny na rynku energii. Dokładamy wszelkich starań, aby dane były aktualne.
                  Sprawdź datę ostatniej aktualizacji cennika w kalkulatorze, aby upewnić się,
                  że porównujesz najbardziej aktualne stawki obowiązujące w Twojej strefie
                  dystrybucyjnej.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="price-freeze-expiration">
              <Accordion.Control>
                <Text fw={600}>Jakie są aktualne ceny energii w 2026 roku?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  <Text c="dimmed" fw={600}>
                    <strong>WAŻNE:</strong> Od 1 stycznia 2026 roku obowiązują nowe, rynkowe ceny
                    energii elektrycznej. Zamrożenie cen zakończyło się 31 grudnia 2025 roku.
                  </Text>
                  <Text c="dimmed">
                    <strong>Aktualne ceny w 2026 roku:</strong>
                  </Text>
                  <List c="dimmed" spacing="xs">
                    <List.Item>
                      <strong>Energia elektryczna:</strong> ~0,61 zł/kWh netto (ceny rynkowe
                      ustalone przez URE)
                    </List.Item>
                    <List.Item>
                      <strong>Opłaty dystrybucyjne:</strong> Wzrost o około 9% względem 2025 roku
                    </List.Item>
                    <List.Item>
                      <strong>Łączny koszt G11:</strong> ~1,13 zł/kWh brutto (energia + dystrybucja + VAT)
                    </List.Item>
                  </List>
                  <Text c="dimmed" mt="sm">
                    <strong>Co to oznacza dla Ciebie?</strong>
                  </Text>
                  <List c="dimmed" spacing="xs">
                    <List.Item>
                      Wszystkie kalkulacje w naszym kalkulatorze używają aktualnych cen na rok 2026
                    </List.Item>
                    <List.Item>
                      Wybór odpowiedniej taryfy jest teraz jeszcze ważniejszy - różnice między
                      taryfami mogą być większe niż w poprzednich latach
                    </List.Item>
                    <List.Item>
                      Taryfy wielostrefowe (G12, G12w) mogą przynieść większe oszczędności przy
                      elastycznym zużyciu energii
                    </List.Item>
                  </List>
                  <Text c="dimmed" size="sm" style={{ fontStyle: 'italic' }} mt="sm">
                    Źródło: Taryfy operatorów sieci dystrybucyjnych zatwierdzone przez Urząd
                    Regulacji Energetyki (URE) na rok 2026.
                  </Text>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="pricing-calculation-method">
              <Accordion.Control>
                <Text fw={600}>Skąd bierze Pan stawki za kWh?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed" mb="sm">
                  W kalkulatorze używamy <strong>"taryfy sprzedawcy"</strong> (seller tariff),
                  która obejmuje pełny koszt energii elektrycznej widoczny na Twoim rachunku.
                  Składa się ona z kilku elementów:
                </Text>
                <List mt="sm" c="dimmed">
                  <List.Item>
                    <strong>Cena energii elektrycznej (produkcja):</strong> ~0,61 zł/kWh netto
                    (ceny rynkowe na rok 2026 zatwierdzone przez URE)
                  </List.Item>
                  <List.Item>
                    <strong>Opłaty dystrybucyjne:</strong> Koszt przesyłu energii przez sieć
                    (różne dla każdej strefy czasowej w taryfie)
                  </List.Item>
                  <List.Item>
                    <strong>Opłata jakościowa:</strong> ~0,035 zł/kWh (utrzymanie jakości energii)
                  </List.Item>
                  <List.Item>
                    <strong>Opłaty stałe miesięczne:</strong> ~9,40 zł/miesiąc (przyłącze jednofazowe)
                  </List.Item>
                </List>
                <Text c="dimmed" mt="sm">
                  Wszystkie składniki są sumowane i przedstawione jako jedna końcowa cena
                  (brutto z VAT 23%) dla każdej taryfy. Dzięki temu widzisz rzeczywisty koszt,
                  jaki zapłacisz za każdą zużytą kilowatogodzinę.
                </Text>
                <Text c="dimmed" mt="sm" size="sm" style={{ fontStyle: 'italic' }}>
                  Źródło danych: Oficjalne taryfy OSD (TAURON®, PGE®, ENEA®, ENERGA®)
                  zatwierdzone przez Urząd Regulacji Energetyki (URE), aktualne na rok 2026.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            {/* Account & Data Questions */}
            <Accordion.Item value="save-calculations">
              <Accordion.Control>
                <Text fw={600}>Jak zapisać wyniki obliczeń?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">
                  Aby zapisać wyniki obliczeń i mieć do nich dostęp w przyszłości, musisz
                  utworzyć bezpłatne konto użytkownika. Po zalogowaniu wszystkie Twoje obliczenia
                  będą automatycznie zapisywane i dostępne w Twoim Panelu Klienta.
                  Możesz tam przeglądać historię obliczeń, porównywać wyniki i śledzić zmiany
                  w czasie.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="data-privacy">
              <Accordion.Control>
                <Text fw={600}>Czy moje dane są bezpieczne?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">
                  Tak, bezpieczeństwo Twoich danych jest naszym priorytetem. Wszystkie dane są
                  przechowywane na bezpiecznych serwerach AWS w regionie europejskim (Frankfurt),
                  zgodnie z wymogami RODO. Stosujemy szyfrowanie połączeń (HTTPS) i nie
                  udostępniamy Twoich danych osobowych żadnym podmiotom trzecim bez Twojej zgody.
                  Nigdy nie przechowujemy haseł do systemów dostawców energii - wszelkie
                  połączenia z eLicznik są przetwarzane w pamięci i natychmiast usuwane.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="delete-account">
              <Accordion.Control>
                <Text fw={600}>Jak mogę usunąć moje konto?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">
                  Możesz w każdej chwili poprosić o usunięcie swojego konta, kontaktując się
                  z nami przez email kontakt@taryfypradu.pl. Zgodnie z RODO masz prawo do
                  usunięcia wszystkich swoich danych osobowych ("prawo do bycia zapomnianym").
                  Usuniemy wszystkie Twoje dane, w tym zapisane obliczenia, profil użytkownika
                  i historię korzystania z platformy w ciągu 30 dni od otrzymania prośby.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            {/* Technical Questions */}
            <Accordion.Item value="guest-mode">
              <Accordion.Control>
                <Text fw={600}>Czy mogę korzystać z kalkulatora bez zakładania konta?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">
                  Tak! Kalkulator można używać bez logowania w trybie gościa. Wprowadź swoje
                  dane zużycia energii, a system obliczy i porówna koszty dla różnych taryf.
                  Pamiętaj jednak, że w trybie gościa wyniki obliczeń nie są zapisywane.
                  Jeśli chcesz mieć dostęp do historii obliczeń i dodatkowych funkcji,
                  warto utworzyć bezpłatne konto.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="mobile-support">
              <Accordion.Control>
                <Text fw={600}>Czy platforma działa na urządzeniach mobilnych?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">
                  Tak, platforma Taryfy Prądu jest w pełni responsywna i działa na wszystkich
                  urządzeniach - smartfonach, tabletach i komputerach. Interfejs automatycznie
                  dostosowuje się do rozmiaru ekranu, zapewniając wygodne korzystanie z kalkulatora
                  niezależnie od urządzenia, którego używasz.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            {/* Billing & Pricing Questions */}
            <Accordion.Item value="cost">
              <Accordion.Control>
                <Text fw={600}>Czy korzystanie z platformy jest płatne?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">
                  Korzystanie z platformy jest obecnie bezpłatne, ale nie wykluczamy wprowadzenia
                  płatnych funkcjonalności w przyszłości. Wszystkie podstawowe funkcje kalkulatora
                  oraz możliwość zapisywania obliczeń pozostaną dostępne bez opłat.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="switch-tariff">
              <Accordion.Control>
                <Text fw={600}>Jak zmienić taryfę u swojego dostawcy energii?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">
                  Po wybraniu najkorzystniejszej taryfy w kalkulatorze możesz ją zmienić,
                  kontaktując się ze swoim dostawcą energii (OSD). Zwykle wymaga to złożenia
                  odpowiedniego wniosku online lub w biurze obsługi klienta. Zmiana taryfy
                  jest bezpłatna, ale może nastąpić dopiero od początku kolejnego miesiąca
                  lub okresu rozliczeniowego. Szczegóły zależą od Twojego dostawcy - sprawdź
                  ich stronę internetową lub zadzwoń do infolinii.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          {/* Contact CTA */}
          <div style={{
            backgroundColor: 'var(--mantine-color-ecoGreen-0)',
            padding: '2rem',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <Text fw={600} size="lg" mb="xs">
              Nie znalazłeś odpowiedzi na swoje pytanie?
            </Text>
            <Text c="dimmed">
              Skontaktuj się z nami przez email:{' '}
              <Text component="a" href="mailto:kontakt@taryfypradu.pl" fw={600} c="ecoGreen">
                kontakt@taryfypradu.pl
              </Text>
            </Text>
          </div>
        </Stack>
      </Container>
    </Layout>
  );
}
